import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface TypewriterProps {
  text: string;
  speed?: number;
  delay?: number;
}

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: var(--pink);
  margin-left: 2px;
  animation: blink 1s step-end infinite;
  vertical-align: middle;

  @keyframes blink {
    from, to { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const TextContainer = styled.span`
  display: inline-block;
`;

export function Typewriter({ text, speed = 100, delay = 0 }: TypewriterProps) {
  // Skip animation for bots, Lighthouse, or reduced-motion users — show text instantly
  const isNoAnimations =
    typeof document !== 'undefined' &&
    (document.documentElement.classList.contains('no-animations') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  const [displayedText, setDisplayedText] = useState(isNoAnimations ? text : '');
  const [started, setStarted] = useState(isNoAnimations);

  useEffect(() => {
    if (isNoAnimations) return;
    const timer = setTimeout(() => {
      setStarted(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, isNoAnimations]);

  useEffect(() => {
    if (!started || isNoAnimations) return;

    if (displayedText.length < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(text.substring(0, displayedText.length + 1));
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [displayedText, text, speed, started, isNoAnimations]);

  return (
    <TextContainer>
      {displayedText}
      <Cursor />
    </TextContainer>
  );
}
